// Cloud EVENT playback — download the event window's MPEG-TS clips and play them
// as a VOD HLS playlist.
//
// For cloud-location devices the recording-agent uploads clips to the cloud recording
// server (recordda → S3) as MPEG-TS (.ts) segments, playable by TIME RANGE (no eid
// correlation needed). Given an event ts we fetch the clips covering [ts−19s, ts+24s],
// download each clip's bytes into a SAME-ORIGIN blob: URL, and compose a VOD m3u8 that
// references those blobs. hls.js loads the (same-origin) blob segments and transmuxes
// the MPEG-TS to fMP4/MSE — we do NOT touch the container format ourselves.
//
// NOTE: the clips are MPEG-TS (first byte 0x47), NOT fragmented MP4. A prior version
// tried to parse them as fMP4 (ftyp/moov/moof/mdat) and failed on every real clip.

import { apiFetch } from '../api';
import { config } from '../../config';
import { fetchCloudClipUrl } from './recRest';

export interface CloudClip {
  clip_id: string;
  clip_start_ts: number; // µs epoch
  clip_end_ts: number; // µs epoch
  has_event: boolean;
  quality?: string;
  url: string; // presigned S3 url
}

/** −19s / +24s FETCH window (µs) — the WORST-CASE range, mirrors lscore
 * CLOUD_EVENT_FETCH_PRE_US/POST_US. Wide because the clip covering event−5s may have
 * started up to 19s before the event, and the clip covering event+10s may end up to
 * 24s after. It is NOT the amount played. */
export const CLOUD_EVENT_PRE_US = 19_000_000;
export const CLOUD_EVENT_POST_US = 24_000_000;
/** −5s / +10s PLAY window (µs) — the event's own 15s, mirrors lscore
 * PLAYBACK_EVENT_PRE_US/POST_US. Only clips overlapping this are played (see
 * LSTimeline.clipsForEventWindow); footage beyond it belongs to other events. */
export const CLOUD_EVENT_PLAY_PRE_US = 5_000_000;
export const CLOUD_EVENT_PLAY_POST_US = 10_000_000;

/** Legacy cleanup (called on logout): the old fMP4 CacheStorage is no longer used —
 * clips now play from transient blob: URLs revoked on switch-away. Delete any stale
 * cache from a prior build. Best-effort. */
export async function clearCloudEventClipCache(): Promise<void> {
  try {
    if (typeof caches !== 'undefined') await caches.delete('ls-cloud-event-fmp4-v1');
  } catch {
    /* ignore */
  }
}

export interface CloudPlaylist {
  /** blob: URL of the composed VOD m3u8 (its segments are same-origin blob: URLs). */
  m3u8Url: string;
  /** Revoke the composed m3u8 + every segment blob URL (call on switch-away/unmount). */
  release: () => void;
  /** Presigned URL of the clip overlapping the event — Safari native-HLS fallback. */
  fallbackUrl?: string;
  /** Clips actually played (overlapping the [−5,+10] play window), ascending by start. */
  clips: CloudClip[];
}

function localOrigin(): string {
  return typeof window !== 'undefined' && window.location?.origin ? window.location.origin : 'https://ls.local';
}

/** In local dev the backend returns absolute dev-edge signed URLs; route recording
 * downloads back through the same-origin API proxy so fetch() reads bytes without
 * localhost CORS noise. In prod this is a no-op (returns the original URL). */
export function mediaFetchUrl(url: string): string {
  try {
    const media = new URL(url);
    const apiBase = new URL(config.apiBase, localOrigin());
    if (apiBase.origin === localOrigin() && media.pathname.startsWith('/v3/recordings/download')) {
      return new URL(media.pathname + media.search, apiBase.origin).toString();
    }
  } catch {
    /* use the original URL */
  }
  return url;
}

/** Download one clip's bytes and expose them as a same-origin blob: URL. Refetches a
 * fresh presigned url once on 403 (expiry). */
export async function clipToBlobUrl(clip: CloudClip, signal?: AbortSignal): Promise<string> {
  let res = await fetch(mediaFetchUrl(clip.url), { signal });
  if (res.status === 403 || res.status === 401) {
    const fresh = await fetchCloudClipUrl(clip.clip_id, signal);
    res = await fetch(mediaFetchUrl(fresh), { signal });
  }
  if (!res.ok) throw new Error(`clip fetch failed: ${res.status}`);
  const bytes = await res.arrayBuffer();
  return URL.createObjectURL(new Blob([bytes], { type: 'video/mp2t' }));
}

/**
 * Fetch the clips for an event and compose a single VOD m3u8 (blob) covering the
 * event's [−5s,+10s] play window. Returns null when no clip covers it.
 *
 * FETCH uses the worst-case [−19,+24] window (so a clip that started well before the
 * event, or ends well after, is still retrieved); PLAYBACK selects only the clips
 * overlapping [event−5s, event+10s] — the event's own 15s — matching native
 * LSTimeline.clipsForEventWindow. The selected clips (usually 1–2) are stitched into
 * ONE m3u8 with NO EXT-X-DISCONTINUITY: they are consecutive segments of the same
 * continuous recording (contiguous, continuous PTS), so hls.js plays them as one
 * stream. (Emitting DISCONTINUITY made hls.js reset the timeline and silently stall
 * at the seam.)
 */
/** The cloud clips that make up an event's [−5s,+10s] play window, ascending by start.
 * Same source/filter playback uses (composeCloudEventPlaylist) — so a download of these
 * clips matches exactly what plays. Empty if the event has no cloud footage. */
export async function fetchCloudEventClips(
  deviceId: string,
  eventTsUs: number,
  signal?: AbortSignal,
): Promise<CloudClip[]> {
  const from = eventTsUs - CLOUD_EVENT_PRE_US; // worst-case fetch window
  const to = eventTsUs + CLOUD_EVENT_POST_US;
  const res = await apiFetch<CloudClip[]>(
    `/v3/devices/${encodeURIComponent(deviceId)}/recordings`,
    { query: { from, to, limit: 50 }, signal },
  );
  const winStart = eventTsUs - CLOUD_EVENT_PLAY_PRE_US; // the event's own 15s: [−5,+10]
  const winEnd = eventTsUs + CLOUD_EVENT_PLAY_POST_US;
  return (res.data ?? [])
    .filter(
      (c) =>
        c.url &&
        c.clip_end_ts > c.clip_start_ts &&
        c.clip_start_ts <= winEnd &&
        c.clip_end_ts >= winStart,
    )
    .sort((a, b) => a.clip_start_ts - b.clip_start_ts);
}

export async function composeCloudEventPlaylist(
  deviceId: string,
  eventTsUs: number,
  signal?: AbortSignal,
): Promise<CloudPlaylist | null> {
  const clips = await fetchCloudEventClips(deviceId, eventTsUs, signal);
  if (clips.length === 0) return null;

  // Download each selected clip to a same-origin blob (avoids per-segment cross-origin
  // fetches by hls.js), then stitch into one m3u8. Revoke all on failure.
  const segUrls: string[] = [];
  try {
    for (const c of clips) segUrls.push(await clipToBlobUrl(c, signal));
  } catch (e) {
    for (const u of segUrls) URL.revokeObjectURL(u);
    throw e;
  }

  const m3u8Url = URL.createObjectURL(
    new Blob([buildVodM3u8(clips, segUrls)], { type: 'application/vnd.apple.mpegurl' }),
  );
  const fallback = clips.find((c) => c.clip_start_ts <= eventTsUs && eventTsUs <= c.clip_end_ts) ?? clips[0];

  return {
    m3u8Url,
    release: () => {
      URL.revokeObjectURL(m3u8Url);
      for (const u of segUrls) URL.revokeObjectURL(u);
    },
    fallbackUrl: fallback?.url,
    clips,
  };
}

/** Compose a VOD HLS playlist stitching the clips as consecutive segments. No
 * EXT-X-DISCONTINUITY: the selected clips are back-to-back segments of the same
 * continuous recording (contiguous, continuous PTS), so hls.js plays them as one
 * stream; a DISCONTINUITY would make it reset the timeline and stall at the seam.
 * segUrls[i] is the media URL for clips[i]. */
export function buildVodM3u8(clips: CloudClip[], segUrls: string[]): string {
  const durs = clips.map((c) => (c.clip_end_ts - c.clip_start_ts) / 1_000_000);
  const maxDur = Math.max(1, Math.ceil(Math.max(...durs)));
  const lines = [
    '#EXTM3U',
    '#EXT-X-VERSION:3',
    '#EXT-X-PLAYLIST-TYPE:VOD',
    `#EXT-X-TARGETDURATION:${maxDur}`,
    '#EXT-X-MEDIA-SEQUENCE:0',
  ];
  clips.forEach((_, i) => {
    lines.push(`#EXTINF:${durs[i].toFixed(3)},`);
    lines.push(segUrls[i]);
  });
  lines.push('#EXT-X-ENDLIST');
  return lines.join('\n') + '\n';
}
